import { gql } from '@/core/shared/api';

import {
  contentOwnership,
  contentOwnershipDetails,
  contentOwnershipWithKeys,
} from './fragments';

export const getContentOwnershipsForBundleCreation = async ({ commit, dispatch }) => {
  commit('setLoading', true);
  commit('contentOwnerships/set', {
    path: 'isFetchingContentOwnerships',
    value: true,
  });

  try {
    var contentOwnershipsForBundleCreation = await dispatch('netGetRequest', { url: '/bundle/all-content-ownerships' }).then(function (response) {
      commit('contentOwnerships/set', { path: 'allContentOwnerships', value: response.data });
      return response.data;
    });
    return contentOwnershipsForBundleCreation;
  } finally {
    commit('setLoading', false);
    commit('contentOwnerships/set', {
      path: 'isFetchingContentOwnerships',
      value: false,
    });
  }
};

export const getContentOwnerships = async ({ commit, dispatch }) => {
  commit('setLoading', true);
  commit('contentOwnerships/set', {
    path: 'isFetchingContentOwnerships',
    value: true,
  });

  const query = `{
    contentOwnerships {
      ${contentOwnership}
    }
  }`;

  try {
    const { contentOwnerships } = await gql.request(query);
    dispatch('insertEntities', {
      name: 'ContentOwnership',
      payload: contentOwnerships,
    });

    return contentOwnerships;
  } finally {
    commit('setLoading', false);
    commit('contentOwnerships/set', {
      path: 'isFetchingContentOwnerships',
      value: false,
    });
  }
};

export const getContentOwnership = async (
  { commit, dispatch },
  contentOwnershipId,
) => {
  commit('setLoading', true);
  commit('contentOwnerships/set', {
    path: 'isFetchingContentOwnership',
    value: true,
  });

  const query = `{
    contentOwnership (contentOwnership: { id: "${contentOwnershipId}" } ) {
      ${contentOwnershipDetails}
    }
  }`;
  try {
    const { contentOwnership } = await gql.request(query);

    dispatch('insertEntities', {
      name: 'ContentOwnership',
      payload: contentOwnership,
    });

    return contentOwnership;
  } finally {
    commit('setLoading', false);
    commit('contentOwnerships/set', {
      path: 'isFetchingContentOwnership',
      value: false,
    });
  }
};

export const lookupSteamId = async ({ commit }, steamId) => {
  commit('setLoading', true);

  const query = `{
    lookupSteamId (steamId: "${steamId}") {
      title
      imageUrl
    }
  }`;

  try {
    const { lookupSteamId } = await gql.request(query);
    return lookupSteamId;
  } finally {
    commit('setLoading', false);
  }
};

export const storeContentOwnership = async (
  { commit, dispatch, rootGetters },
  contentOwnership,
) => {
  const formattedContentOwnership = {
    ...contentOwnership,
    contentOwnershipLanguages: contentOwnership.contentOwnershipLanguages?.filter(
      ({ language }) => Boolean(language)
    ),
  };
  if (
    contentOwnership._imageBlob ||
    contentOwnership._videoBlob ||
    contentOwnership._gameplayVideoBlob
  ) {
    return dispatch(
      'storeContentOwnershipWithMedia',
      formattedContentOwnership,
    );
  }
  commit('setLoading', true);

  const mutation = `mutation storeContentOwnership ($contentOwnership: ContentOwnershipInput) {
    storedContentOwnership: storeContentOwnership (contentOwnership: $contentOwnership) {
      ${contentOwnershipDetails}
    }
  }`;

  try {
    const { storedContentOwnership } = await gql.request(mutation, {
      contentOwnership: formattedContentOwnership,
    });
    dispatch('insertEntities', {
      name: 'ContentOwnership',
      payload: storedContentOwnership,
    });
    return storedContentOwnership;
  } finally {
    commit('setLoading', false);
  }
};

export const createContentOwnership = async (
  { commit, dispatch },
  contentOwnership,
) => {
  const { _imageBlob, ...contentOwnershipToSave } = contentOwnership;
  commit('setLoading', true);

  const mutation = `mutation storeContentOwnership ($contentOwnership: ContentOwnershipInput) {
    storeContentOwnership (contentOwnership: $contentOwnership) {
      id
      title
      imageUrl
      status
    }
  }`;
  const variables = {
    contentOwnership: {
      ...contentOwnershipToSave,
      imageUrl: contentOwnership._imageBlob && contentOwnership._imageBlob.src,
    },
  };
  try {
    const { storeContentOwnership } = await gql.request(
      mutation,
      variables,
    );

    dispatch('insertEntities', {
      name: 'ContentOwnership',
      payload: storeContentOwnership,
    });

    return storeContentOwnership;
  } finally {
    commit('setLoading', false);
  }
};

export const storeContentOwnershipWithMedia = async (
  { commit, dispatch },
  contentOwnership,
) => {
  commit('setLoading', true);
  const {
    _imageBlob,
    _videoBlob,
    _gameplayVideoBlob,
    ...contentOwnershipToSave
  } = contentOwnership;
  const blobs = [_imageBlob, _videoBlob, _gameplayVideoBlob].filter((v) => !!v);

  const variables = {
    contentOwnership: {
      ...contentOwnershipToSave,
      imageBlobFieldName: _imageBlob?.name,
      videoBlobFieldName: _videoBlob?.name,
      gameplayVideoBlobFieldName: _gameplayVideoBlob?.name,
    },
  };

  const mutation = `mutation storeContentOwnership ($contentOwnership: ContentOwnershipInput) {
    storeContentOwnership (contentOwnership: $contentOwnership) {
      ${contentOwnershipDetails}
    }
  }`;

  try {
    const { storeContentOwnership } = await gql.requestWithBlobs(
      mutation,
      variables,
      blobs,
      { timeout: false },
    );

    dispatch('insertEntities', {
      name: 'ContentOwnership',
      payload: storeContentOwnership,
    });

    return storeContentOwnership;
  } finally {
    commit('setLoading', false);
  }
};

export const getGenres = async ({ commit }) => {
  commit('setLoading', true);

  try {
    const { genres } = await gql.request('{ genres { id title } }');
    return genres;
  } finally {
    commit('setLoading', false);
  }
};

export const verifyContentOwnership = async (
  { commit, dispatch },
  contentOwnership,
) => {
  commit('setLoading', true);

  const query = `
    mutation verifyContentOwnership ($contentOwnership: ContentOwnershipInput) {
      verifyContentOwnership (contentOwnership: $contentOwnership) {
        id
        status
      }
    }
  `;

  try {
    const { verifyContentOwnership } = await gql.request(query, {
      contentOwnership,
    });

    dispatch('insertEntities', {
      name: 'ContentOwnership',
      payload: verifyContentOwnership,
    });

    return verifyContentOwnership;
  } finally {
    commit('setLoading', false);
  }
};

export const uploadGameKeys = async (
  { commit, dispatch },
  { contentOwnership, file },
) => {
  commit('setLoading', true);

  const query = `mutation uploadGameKeys ($contentOwnership: ContentOwnershipInput) {
    uploadGameKeys (contentOwnership: $contentOwnership) {
      ${contentOwnershipWithKeys}
    }
  }`;

  try {
    const { uploadGameKeys } = await gql.requestWithBlobs(
      query,
      { contentOwnership, blob: null },
      {
        name: 'blob',
        file,
      },
    );

    dispatch('insertEntities', {
      name: 'ContentOwnership',
      payload: uploadGameKeys,
    });

    return uploadGameKeys;
  } finally {
    commit('setLoading', false);
  }
};

export const removePendingKeys = async (
  { commit, dispatch },
  contentOwnership,
) => {
  commit('setLoading', true);

  const query = `mutation removePendingKeys ($contentOwnership: ContentOwnershipInput) {
    removePendingKeys (contentOwnership: $contentOwnership) {
      ${contentOwnershipWithKeys}
    }
  }`;

  try {
    const { removePendingKeys } = await gql.request(query, {
      contentOwnership,
    });

    dispatch('insertEntities', {
      name: 'ContentOwnership',
      payload: removePendingKeys,
    });

    return removePendingKeys;
  } finally {
    commit('setLoading', false);
  }
};

export const commitPendingKeys = async (
  { commit, dispatch },
  contentOwnership,
) => {
  commit('setLoading', true);

  const query = `mutation commitPendingKeys ($contentOwnership: ContentOwnershipInput) {
    commitPendingKeys (contentOwnership: $contentOwnership) {
      ${contentOwnershipWithKeys}
    }
  }`;

  try {
    const { commitPendingKeys } = await gql.request(query, {
      contentOwnership,
    });

    dispatch('insertEntities', {
      name: 'ContentOwnership',
      payload: commitPendingKeys,
    });

    return commitPendingKeys;
  } finally {
    commit('setLoading', false);
  }
};

/** Weird Formatting, I know. but building up queries is a bit of a drag. I've commented below to make more sense. */
export const createPrices = async ({ commit }, contentOwnership) => {
  if (
    contentOwnership.upcomingPrices &&
    contentOwnership.upcomingPrices.length > 0
  ) {
    commit('setLoading', true);

    let makeCall = false;

    //  First, make the mutation.
    let query = 'mutation createContentOwnershipPrices {';

    // Loop through all the upcomingPrices and make a mutation for them, with the country name as the GQL alias
    contentOwnership.upcomingPrices.forEach((price) => {
      if (!price.id) {
        makeCall = true;
        query += `  ${price.country}: createContentOwnershipPrice ( contentOwnershipPrice: { ownership: { id: "${contentOwnership.id}" }, `;
        query += `priceMinute: ${price.priceMinute}, priceMinuteOverride: ${price.priceMinuteOverride}, `;
        query += `priceMonth: ${price.priceMonth}, priceMonthOverride: ${price.priceMonthOverride}, `;
        query += `priceYear: ${price.priceYear}, priceYearOverride: ${price.priceYearOverride}, `;
        query += `distribute: ${price.distribute}, country: "${price.country}" }) { id }`;
        query += `
        `;
      }
    });

    // Close up the query. and send it off!
    query += '}';

    if (makeCall) {
      await gql.request(query, {}, { timeout: false });
    }
    commit('setLoading', false);
  }
};

export const createContentOwnershipAgreement = async (
  { commit },
  contentOwnership,
) => {
  if (contentOwnership.upcomingAgreement) {
    commit('setLoading', true);
    const query = `mutation storeContentOwnershipAgreement ($contentOwnershipAgreement: ContentOwnershipAgreementInput) {
      createContentOwnershipAgreement (contentOwnershipAgreement: $contentOwnershipAgreement) {
        id
        content
      }
    }`;

    const contentOwnershipAgreement = {
      contentOwnership: {
        id: contentOwnership.id,
      },
      content: contentOwnership.upcomingAgreement.content,
    };

    try {
      const { createContentOwnershipAgreement } = await gql.request(query, {
        contentOwnershipAgreement,
      });
      return createContentOwnershipAgreement;
    } finally {
      commit('setLoading', false);
    }
  }
};
